// The one thing to do next in a project, for the dashboard's Next step tile. Pure, so it can be tested: the workflow is
// upload → annotate and review 50 annotations → train → auto-annotate the rest → review → train again.
import type { AnnotationStats } from "../api/ml";
import { MIN_REVIEWED } from "./training";

export type NextAction = "upload" | "review" | "annotate" | "train" | "auto" | "deploy";

export interface NextStep {
  action: NextAction;
  /** The tile's headline: an instruction, under about six words. */
  title: string;
  /** One sentence on why, or what it unlocks. */
  detail: string;
  /** The button on the tile. */
  button: string;
  /** 0–1 towards the reviewed annotations training needs, when that is what the step is about. */
  progress?: number;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function nextStep(s: Pick<AnnotationStats,
  "total" | "unannotated" | "auto_annotated" | "reviewed_annotation_count" | "can_train" | "has_trained_model" | "latest_model_version">,
): NextStep {
  const reviewed = s.reviewed_annotation_count;
  const progress = Math.min(reviewed, MIN_REVIEWED) / MIN_REVIEWED;
  if (s.total === 0) {
    return { action: "upload", title: "Upload your images", detail: "Start with the pictures the model should learn from. JPEG, PNG and WebP work.", button: "Upload images" };
  }
  if (s.auto_annotated > 0) {
    return {
      action: "review", title: `Review ${plural(s.auto_annotated, "image")}`,
      detail: "Their annotations, from the model or an import, are not reviewed yet. Check and correct them: only reviewed annotations are used for training.", button: "Show them",
    };
  }
  if (!s.can_train) {
    const need = Math.max(MIN_REVIEWED - reviewed, 0);
    return {
      action: "annotate", title: `Annotate ${need} more to train`, progress,
      detail: `Training needs ${MIN_REVIEWED} reviewed annotations; you have ${reviewed}.`,
      button: s.unannotated > 0 ? "Annotate next image" : "Open annotations",
    };
  }
  if (!s.has_trained_model) {
    return { action: "train", title: "Train your first model", detail: `You have ${reviewed} reviewed annotations, enough to start.`, button: "Train", progress: 1 };
  }
  const v = s.latest_model_version ? `v${s.latest_model_version}` : "the model";
  if (s.unannotated > 0) {
    return {
      action: "auto", title: `Auto-annotate ${plural(s.unannotated, "image")}`,
      detail: `Let ${v} annotate the rest, then review what it found.`, button: "Auto-annotate",
    };
  }
  return { action: "deploy", title: `${v} is ready`, detail: "Every image is annotated and reviewed. Export the model for the line, or train again to improve it.", button: "Export for deployment" };
}
